import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { starteAnalyseApi, fetchAnalyseHistorieApi } from './analyseApi';

/**
 * Redux-Slice für die Aktien-Tiefenanalyse.
 *
 * `starteAnalyse` stößt eine Live-Analyse an (Yahoo als Pflichtquelle +
 * Reddit-Buzz) und liefert das verdichtete Gesamturteil. Schlägt eine
 * Yahoo-Anfrage fehl, liefert das Backend Fehlerdetails (HTTP-Status + Body),
 * die hier als `fehlerDetails` abgelegt und im Frontend angezeigt werden.
 */

export const starteAnalyse = createAsyncThunk(
  'analyse/starten',
  async ({ name, isin }, { rejectWithValue }) => {
    try {
      return await starteAnalyseApi({ name, isin });
    } catch (err) {
      return rejectWithValue(normalisiereFehler(err));
    }
  }
);

/**
 * Vereinheitlicht Fehler zu { message, details }. Unterscheidet die App-Antwort
 * (ApiResponse mit FehlerDetails) von einem Gateway-Fehler (z.B. nginx-502 mit
 * HTML-Body) und einem reinen Netzwerkfehler – damit immer der echte Body sichtbar wird.
 */
function normalisiereFehler(err) {
  const antwort = err.response;
  if (!antwort) {
    return { message: err.message, details: null };
  }
  const body = antwort.data;
  if (body && typeof body === 'object') {
    return { message: body.message || err.message, details: body.data || null };
  }
  // Nicht-JSON-Body (z.B. nginx „Bad Gateway"-HTML) trotzdem anzeigen.
  return {
    message: err.message,
    details: {
      quelle: 'Gateway/Proxy',
      httpStatus: antwort.status,
      responseBody: typeof body === 'string' && body ? body : null,
    },
  };
}

export const fetchAnalyseHistorie = createAsyncThunk('analyse/historie', async (isin) => {
  return (await fetchAnalyseHistorieApi(isin)) ?? [];
});

const initialState = {
  ergebnis: null,
  status: 'idle', // idle | loading | succeeded | failed
  error: null,
  fehlerDetails: null, // { quelle, httpStatus, responseBody } bei Quellen-Fehlern
  historie: [],
  historieStatus: 'idle',
};

const analyseSlice = createSlice({
  name: 'analyse',
  initialState,
  reducers: {
    analyseZuruecksetzen: (state) => {
      state.ergebnis = null;
      state.status = 'idle';
      state.error = null;
      state.fehlerDetails = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(starteAnalyse.pending, (state) => {
        state.status = 'loading';
        state.error = null;
        state.fehlerDetails = null;
      })
      .addCase(starteAnalyse.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.ergebnis = action.payload;
      })
      .addCase(starteAnalyse.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload?.message ?? action.error.message;
        state.fehlerDetails = action.payload?.details ?? null;
      })
      .addCase(fetchAnalyseHistorie.pending, (state) => {
        state.historieStatus = 'loading';
      })
      .addCase(fetchAnalyseHistorie.fulfilled, (state, action) => {
        state.historieStatus = 'succeeded';
        state.historie = action.payload;
      })
      .addCase(fetchAnalyseHistorie.rejected, (state) => {
        state.historieStatus = 'failed';
      });
  },
});

export const { analyseZuruecksetzen } = analyseSlice.actions;

export const selectAnalyseErgebnis = (state) => state.analyse.ergebnis;
export const selectAnalyseStatus = (state) => state.analyse.status;
export const selectAnalyseError = (state) => state.analyse.error;
export const selectAnalyseFehlerDetails = (state) => state.analyse.fehlerDetails;
export const selectAnalyseHistorie = (state) => state.analyse.historie;

export default analyseSlice.reducer;
